import React, { useEffect, useState } from "react";
// Trigger Webpack rebuild
import { useLocation, useNavigate } from "react-router-dom";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";

interface ProbationHeaderProps {
  title: string;
  subtitle: string;
  selectedCycleId: string | number;
  onCycleChange: (id: string | number, currentCycle: any, cyclesList: any[]) => void;
  extra?: React.ReactNode;
  showStatusBadge?: boolean;
  refreshTrigger?: number;
}

export const ProbationHeader: React.FC<ProbationHeaderProps> = ({
  title,
  subtitle,
  selectedCycleId,
  onCycleChange,
  extra,
  showStatusBadge = false,
  refreshTrigger = 0
}) => {
  const [cyclesList, setCyclesList] = useState<any[]>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  // Load cycles list
  useEffect(() => {
    const fetchCycles = async () => {
      try {
        const response = await Api.get(API_URLS.PROBATION.CYCLES, true);
        const resData = response?.data || response;
        const innerData = resData?.data || resData;

        let list: any[] = [];
        if (Array.isArray(innerData)) {
          list = innerData;
        } else if (Array.isArray(innerData?.items)) {
          list = innerData.items;
        }

        setCyclesList(list);

        if (list.length > 0) {
          const stateId = location.state?.cycleId;
          const queryId = queryParams.get("cycleId");
          const currentId = stateId || queryId || "";
          
          let targetId = currentId || list[0].id;
          const found = list.find(c => String(c.id) === String(targetId));
          if (!found) {
            targetId = list[0].id;
          }
          
          // Trigger initial cycle change
          const activeCycle = list.find(c => String(c.id) === String(targetId));
          onCycleChange(targetId, activeCycle, list);
        }
      } catch (e) {
        console.error("Failed to load cycles list", e);
      }
    };
    fetchCycles();
  }, [refreshTrigger]);

  // Sync selection when query param or location state changes
  useEffect(() => {
    if (cyclesList.length === 0) return;
    const stateId = location.state?.cycleId;
    const queryId = queryParams.get("cycleId");
    const currentId = stateId || queryId || "";
    if (currentId && String(currentId) !== String(selectedCycleId)) {
      const activeCycle = cyclesList.find(c => String(c.id) === String(currentId));
      onCycleChange(currentId, activeCycle, cyclesList);
    }
  }, [location.state, location.search, cyclesList, selectedCycleId]);

  const handleCycleChange = (id: string | number) => {
    if (!id) return;
    const activeCycle = cyclesList.find(c => String(c.id) === String(id));
    onCycleChange(id, activeCycle, cyclesList);
    navigate(`${location.pathname}?cycleId=${id}`, { replace: true, state: { cycleId: id } });
  };

  const currentCycle = cyclesList.find(c => String(c.id) === String(selectedCycleId));

  return (
    <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-3 mb-3">
      <div className="my-auto">
        <h3 className="page-title mb-1">{title}</h3>
        <p className="text-muted small mb-0">{subtitle}</p>
      </div>
      <div className="d-flex flex-wrap align-items-center gap-2 w-100 w-sm-auto justify-content-start justify-content-sm-end">
        <select
          className="form-select form-select-sm flex-fill flex-sm-grow-0"
          value={selectedCycleId}
          onChange={(e) => handleCycleChange(e.target.value)}
          style={{ minWidth: 200, maxWidth: 300, height: 36, textOverflow: "ellipsis" }}
        >
          <option value="">-- Select Cycle --</option>
          {cyclesList.map((c) => {
            let displayTitle = c.title || `Cycle #${c.id} (${c.financialYear || ""})`;
            if (displayTitle.includes("[")) {
              displayTitle = displayTitle.substring(0, displayTitle.indexOf("[")).trim();
            }
            return (
              <option key={c.id} value={c.id}>
                {displayTitle}
              </option>
            );
          })}
        </select>
        {showStatusBadge && currentCycle && (
          <span className={`badge ${
            currentCycle.status === "REVIEWED" || currentCycle.status === "COMPETENT_AUTHORITY_APPROVAL" || currentCycle.status === "APPROVED" || currentCycle.status === "COMPLETED"
              ? "bg-success"
              : "bg-info text-white"
          }`} style={{ fontSize: "12px", padding: "8px 12px", borderRadius: "6px" }}>
            {currentCycle.status}
          </span>
        )}
        {extra}
      </div>
    </div>
  );
};
